import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  to, 
  href, 
  fullWidth, 
  icon,
  iconPosition = 'left',
  ...props 
}) => {
  // Determine if button should be a Link, anchor, or button
  if (to) {
    return (
      <StyledLink 
        to={to} 
        $variant={variant} 
        $size={size} 
        $fullWidth={fullWidth} 
        $hasIcon={!!icon}
        $iconPosition={iconPosition}
        {...props}
      >
        {icon && iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
        <span>{children}</span>
        {icon && iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
      </StyledLink>
    );
  }
  
  if (href) {
    return (
      <StyledAnchor 
        href={href} 
        $variant={variant} 
        $size={size} 
        $fullWidth={fullWidth}
        $hasIcon={!!icon}
        $iconPosition={iconPosition}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {icon && iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
        <span>{children}</span>
        {icon && iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
      </StyledAnchor>
    );
  }
  
  return (
    <StyledButton 
      $variant={variant} 
      $size={size} 
      $fullWidth={fullWidth}
      $hasIcon={!!icon}
      $iconPosition={iconPosition}
      {...props}
    >
      {icon && iconPosition === 'left' && <IconWrapper>{icon}</IconWrapper>}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <IconWrapper>{icon}</IconWrapper>}
    </StyledButton>
  );
};

// Shared button styles
const buttonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: all ${({ theme }) => theme.transitions.medium};
  cursor: pointer;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  text-align: center;
  
  // Variant styles
  ${({ $variant, theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.text.primary};
          
          &:hover {
            background-color: #a01930; // Darker red
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.md};
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      case 'secondary':
        return css`
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.text.primary};
          
          &:hover {
            background-color: #c5461c; // Darker orange
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.md};
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      case 'tertiary':
        return css`
          background-color: ${theme.colors.tertiary};
          color: ${theme.colors.text.dark};
          
          &:hover {
            background-color: #e09a1f; // Darker gold
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.md};
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: ${theme.colors.primary};
          border: 2px solid ${theme.colors.primary};
          
          &:hover {
            background-color: ${theme.colors.primary};
            color: ${theme.colors.text.primary};
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.md};
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      case 'ghost':
        return css`
          background-color: transparent;
          color: ${theme.colors.primary};
          
          &:hover {
            background-color: rgba(196, 30, 58, 0.1);
            transform: translateY(-2px);
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      case 'dark':
        return css`
          background-color: ${theme.colors.background.dark};
          color: ${theme.colors.text.primary};
          
          &:hover {
            background-color: #333;
            transform: translateY(-2px);
            box-shadow: ${theme.shadows.md};
          }
          
          &:active {
            transform: translateY(0);
          }
        `;
      default:
        return css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.text.primary};
        `;
    }
  }}
  
  // Size styles
  ${({ $size, theme }) => {
    switch ($size) {
      case 'small':
        return css`
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
        `;
      case 'medium':
        return css`
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
        `;
      case 'large':
        return css`
          padding: 1rem 2rem;
          font-size: 1.125rem;
        `;
      default:
        return css`
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
        `;
    }
  }}
  
  // Icon styles
  ${({ $hasIcon, $iconPosition }) => {
    if ($hasIcon) {
      return css`
        flex-direction: ${$iconPosition === 'right' ? 'row' : 'row-reverse'};
        justify-content: ${$iconPosition === 'right' ? 'space-between' : 'space-between'};
      `;
    }
  }}
  
  // Disabled state
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;

const StyledButton = styled.button`
  ${buttonStyles}
`;

const StyledLink = styled(Link)`
  ${buttonStyles}
  text-decoration: none;
`;

const StyledAnchor = styled.a`
  ${buttonStyles}
  text-decoration: none;
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  margin: ${({ $iconPosition }) => 
    $iconPosition === 'right' ? '0 0 0 0.5rem' : '0 0.5rem 0 0'};
`;

export default Button;
